'use client';

import {
  useCallback,
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { database } from '@/_libs/firebase';
import { ref, onValue } from 'firebase/database';
import {
  getMessages,
  markConversationSeen,
  markThreadSeen,
  sendMessage as APIsendMessage,
  getSmsStatus,
} from '@/_api/client-conversations';
import {
  getV2Conversations,
  getV2ConversationsCount,
} from '@/_api/v2/conversations';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { ExportConversationDialog } from './components/ExportConversationDialog';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { markAsUnseen } from '@/_api/admin-conversations';
import type { AdminConversationsProviderProps } from './AdminConversationsProvider.types';
import type { Conversation } from '@/_types/models/conversations';

const DEBOUNCE_TIMEOUT = 1000 as const;

const AdminConversationsContext =
  createContext<AdminConversationsProviderProps>({
    clearConversations: () => null,
    conversationIdNumber: undefined,
    conversations: [],
    isLoading: false,
    isMessagesLoading: false,
    isSending: false,
    onToggleExporting: () => null,
    reloadConversations: () => null,
    reloadMessages: () => null,
    reloadUnseenCount: () => Promise.resolve(),
    markConversationAsUnseen: () => null,
    selectedMessage: undefined,
    sendMessage: (_text: string) => undefined,
    setSelectedMessage: () => null,
    setThreadSeen: (_conversationId: number, _threadId: number) =>
      Promise.resolve(),
    unseenCount: 0,
  });

const AdminConversationsProvider: any = ({ children }: any) => {
  const [unseenCount, setUnseenCount] = useState<number>(0);

  const { conversationId } = useParams();
  const { selectedHash, refreshCompanyConversations } =
    useContext(CompanyContext);
  const { push } = useRouter();

  const { user, token, isAdvisor } = useContext(UserContext);
  const conversationSearchParam = useSearchParams()?.get('conversationId');

  const conversationIdNumber: number | undefined = useMemo(
    () =>
      conversationId || conversationSearchParam
        ? parseInt(
            (conversationId as string) || (conversationSearchParam as string),
          )
        : undefined,
    [conversationId, conversationSearchParam],
  );

  const [isExporting, setExporting] = useState<boolean>(false);
  const [isMessagesLoading, setMessagesLoading] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isSending, setSending] = useState<boolean>(false);
  const [conversations, setConversations] = useState<Array<Conversation>>([]);
  const [selectedMessage, setSelectedMessage] = useState<
    Conversation | undefined
  >(undefined);

  const reloadUnseenCount = useCallback(async (): Promise<void> => {
    if (!token || !isAdvisor) return;

    try {
      const { unread_count } = await getV2ConversationsCount({ token });

      setUnseenCount(unread_count ?? 0);
    } catch (error) {
      console.error(error);
    }
  }, [token, isAdvisor]);

  const markConversationAsUnseen = useCallback(
    (conversationId: number) => {
      markAsUnseen({ id: conversationId, token });
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation && conversation.last_message) {
        const lastMessage = conversation.last_message;
        if (lastMessage && !lastMessage.unseen_user_ids.includes(user?.id!)) {
          lastMessage.unseen_user_ids.push(user?.id!);
        }
        setConversations([...conversations]);
      }
    },
    [token, conversations, user?.id],
  );

  const sendMessage = async (
    text: string,
    selectedMessageId?: number | null,
    date?: string | null,
    messageTag?: string,
    hasReminder?: boolean,
  ): Promise<void> => {
    if (!text) return;

    setSending(true);

    try {
      const baseParams = {
        conversationId: selectedMessage?.id ?? (conversationIdNumber as number),
        text,
        messageTag: messageTag ?? 'general',
        hasReminder: hasReminder ?? false,
        messageThreadId: selectedMessageId || undefined,
        scheduledTime: date || null,
      };

      await APIsendMessage({
        ...baseParams,
        token,
      });
      await loadMessages(true);
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  // Poll for SMS status updates
  const pollSmsStatus = useCallback(async () => {
    if (!selectedMessage?.id || !token || !conversationIdNumber) return;

    try {
      const smsStatusData = await getSmsStatus({
        conversationId: selectedMessage?.id ?? (conversationIdNumber as number),
        token,
      });

      if (
        smsStatusData &&
        !smsStatusData.error &&
        (smsStatusData as any).messages?.length > 0
      ) {
        const smsMessages = (smsStatusData as any).messages;

        // Update the selectedMessage (current conversation being viewed)
        setSelectedMessage((prevSelected) => {
          if (!prevSelected || !prevSelected.messages) return prevSelected;

          const updatedMessages = prevSelected.messages.map((message) => {
            const statusUpdate = smsMessages.find(
              (m: any) => m.id === message.id,
            );
            if (statusUpdate) {
              return {
                ...message,
                sms_delivery_status: statusUpdate.sms_delivery_status,
              };
            }
            return message;
          });

          return { ...prevSelected, messages: updatedMessages };
        });

        // Also update the conversations array for consistency
        setConversations((prevConversations) => {
          return prevConversations.map((conversation) => {
            if (
              conversation.id ===
                (selectedMessage?.id ?? conversationIdNumber) &&
              conversation.messages
            ) {
              const updatedMessages = conversation.messages.map((message) => {
                const statusUpdate = smsMessages.find(
                  (m: any) => m.id === message.id,
                );
                if (statusUpdate) {
                  return {
                    ...message,
                    sms_delivery_status: statusUpdate.sms_delivery_status,
                  };
                }
                return message;
              });
              return { ...conversation, messages: updatedMessages };
            }
            return conversation;
          });
        });
      }
    } catch (error) {
      console.error('Failed to poll SMS status:', error);
    }
  }, [selectedMessage?.id, token, conversationIdNumber]);

  const loadMessages = useCallback(
    async (isSilent?: boolean, page?: number) => {
      if ((!conversationIdNumber && !selectedHash) || !token) return;
      if (!isSilent) setSelectedMessage(undefined);

      try {
        if (!isSilent) setMessagesLoading(true);

        const conversations = await getV2Conversations({ token, page });

        setConversations(conversations);
        refreshCompanyConversations(undefined, true);

        if (conversationIdNumber) {
          const conversation = await getMessages({
            conversationId: conversationIdNumber,
            token,
          });

          const sortedConversationWithMessages = {
            ...conversation,
            messages: conversation.messages?.sort((a, b) => {
              const dateA = new Date(a.created_at).getTime();
              const dateB = new Date(b.created_at).getTime();
              return dateA - dateB;
            }),
          };

          setSelectedMessage(sortedConversationWithMessages);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setMessagesLoading(false);
      }
    },
    [conversationIdNumber, selectedHash, refreshCompanyConversations, token],
  );

  const updateUnseenMessages = useCallback(
    (conversationId: number, threadId?: number) => {
      setConversations((prevConversations) =>
        prevConversations.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;

          return {
            ...conversation,
            messages: conversation.messages?.map((message) => {
              if (threadId && message.message_thread_id !== threadId) {
                return message;
              }

              return {
                ...message,
                unseen_user_ids: message.unseen_user_ids.filter(
                  (id) => id !== user?.id,
                ),
              };
            }),
            unseen_messages: conversation.unseen_messages?.map((message) => {
              if (threadId && message.message_thread_id !== threadId) {
                return message;
              }

              return {
                ...message,
                unseen_user_ids: message.unseen_user_ids.filter(
                  (id) => id !== user?.id,
                ),
              };
            }),
          };
        }),
      );

      setSelectedMessage((prev) => {
        if (!prev || prev.id !== conversationId) return prev;
        return {
          ...prev,
          messages: prev.messages?.map((message) => {
            if (threadId && message.message_thread_id !== threadId) {
              return message;
            }
            return {
              ...message,
              unseen_user_ids: message.unseen_user_ids.filter(
                (id) => id !== user?.id,
              ),
            };
          }),
          unseen_messages: prev.unseen_messages?.map((message) => {
            if (threadId && message.message_thread_id !== threadId) {
              return message;
            }
            return {
              ...message,
              unseen_user_ids: message.unseen_user_ids.filter(
                (id) => id !== user?.id,
              ),
            };
          }),
        };
      });
    },
    [user?.id],
  );

  const setConversationSeen = useCallback(
    async (conversationId: number) => {
      if (!conversationId || !token) return;

      try {
        await markConversationSeen({
          conversationId,
          token,
        });

        reloadUnseenCount();
        updateUnseenMessages(conversationId);
      } catch (error) {
        console.error(error);
      }
    },
    [token, reloadUnseenCount, updateUnseenMessages],
  );

  const setThreadSeen = useCallback(
    async (conversationId: number, threadId: number) => {
      if (!conversationId || !threadId || !token) return;

      try {
        await markThreadSeen({
          conversationId,
          threadId,
          token,
        });
        updateUnseenMessages(conversationId, threadId);
        loadMessages(true);
      } catch (error) {
        console.error(error);
      }
    },
    [token, updateUnseenMessages, loadMessages],
  );

  const onToggleExporting = () => setExporting(!isExporting);

  const reloadConversations = useCallback(
    async (isSilent: boolean = false) => {
      try {
        if (!isSilent) setLoading(true);
        const conversations = await getV2Conversations({ token });

        if (conversations?.error) return;

        setConversations(conversations);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const orderedConversations: Array<Conversation> = useMemo(
    () =>
      Array.isArray(conversations)
        ? [...conversations]
            .filter((convo) => convo.last_message_datetime)
            .sort(
              (a, b) =>
                new Date(b?.last_message_datetime || '').getTime() -
                new Date(a?.last_message_datetime || '').getTime(),
            )
            .concat(
              // simply move all empty conversations to the end
              [...conversations].filter(
                (convo) => !convo.last_message_datetime,
              ),
            )
        : [],
    [conversations],
  );

  const clearConversations = useCallback((): void => {
    if (typeof selectedMessage?.id === 'number') {
      setSelectedMessage(undefined);
    }
  }, [selectedMessage?.id]);

  useEffect(() => {
    conversationIdNumber && setConversationSeen(conversationIdNumber);
  }, [token, loadMessages, setConversationSeen, conversationIdNumber]);

  useEffect(() => {
    selectedHash && loadMessages();
  }, [selectedHash, loadMessages]);

  useEffect(() => {
    if (conversationSearchParam) {
      push(`/admin/peak/messages/${conversationSearchParam}`);
    }
  }, [conversationSearchParam, push]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let unsubscribe: (() => void) | undefined;

    const loadConversations = async (): Promise<void> => {
      if (!token || !user?.id) return;

      try {
        const conversations = await getV2Conversations({ token });

        if (!conversations?.error) {
          setConversations(conversations);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        reloadUnseenCount();
      }
    };

    const startRealtimeListener = (): void => {
      if (!token || !user?.id) return;

      const messagesRef = ref(database, `users/${user.id}/newMessages`);
      const handleUpdate = async (_snapshot: any): Promise<void> => {
        clearTimeout(timeoutId);

        timeoutId = setTimeout(async () => {
          await loadConversations();
          await loadMessages(true);
        }, DEBOUNCE_TIMEOUT);
      };

      unsubscribe = onValue(messagesRef, handleUpdate);
    };

    token && startRealtimeListener();

    return () => {
      clearTimeout(timeoutId);

      unsubscribe && unsubscribe();
    };
  }, [loadMessages, token, conversationIdNumber, reloadUnseenCount, user?.id]);

  // SMS status polling effect
  useEffect(() => {
    if ((!selectedMessage?.id && !conversationIdNumber) || !token) return;

    // Start polling immediately
    pollSmsStatus();

    // Set up interval to poll every 10 seconds
    const pollInterval = setInterval(pollSmsStatus, 10000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [selectedMessage?.id, conversationIdNumber, token, pollSmsStatus]);

  return (
    <AdminConversationsContext
      value={{
        clearConversations,
        conversationIdNumber,
        conversations: orderedConversations,
        isMessagesLoading,
        isLoading,
        isSending,
        onToggleExporting,
        reloadConversations,
        markConversationAsUnseen,
        reloadUnseenCount,
        reloadMessages: loadMessages,
        selectedMessage,
        sendMessage,
        setSelectedMessage,
        setThreadSeen,
        unseenCount,
      }}
    >
      <ExportConversationDialog
        isOpen={isExporting}
        onClose={onToggleExporting}
      />
      {children}
    </AdminConversationsContext>
  );
};

export { AdminConversationsContext, AdminConversationsProvider };
